import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult, TpETLDryRunObject } from '../../interfaces';
import { addDryRunObject, getETLComponentInputObjectTable } from '../../utils';
import { createConfigParamTypeExistingColumn, createConfigParamTypeExistingColumnList } from '../utils';
import { addETLDryRunError } from '../../etlDryRun';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const matrix: TpETLDryRunObject = {
    type: TpETLObjectTypes.MATRIX,
    id: comp.outputObjectIds['result'],
    createdByCompId: comp.id,
    obsMetadataColumns: [],
    featureMetadataColumns: [],
    linkColumnId: comp.params['linkColumn'],
  };
  addDryRunObject(dryRun, matrix);

  // Fill in the features metadata columns
  const metadataTable = getETLComponentInputObjectTable(dryRun, comp, 'metadataTable');
  matrix.featureMetadataColumns = metadataTable.columns.map((col) => structuredClone(col));

  if (!comp.params['linkColumn']) {
    addETLDryRunError(dryRun, `Missing link column`, comp.id, 'linkColumn');
    return { abort: false };
  }

  // Fill in the observation metadata columns
  const observationsTable = getETLComponentInputObjectTable(dryRun, comp, 'observationTable');
  const metadataColumnIds = comp.params['observationMetadataColumns'] as string[];
  matrix.obsMetadataColumns = observationsTable.columns
    .filter((col) => metadataColumnIds.includes(col.identifier))
    .map((col) => structuredClone(col));

  return {
    abort: false,
  };
}

export const manifestETLTablesToMatrix: TpETLComponentTypeManifest = {
  id: 'TablesToMatrix',
  name: 'Create a matrix',
  description: 'Combines an observations table and feature metadata table into a matrix.',
  categoryId: 'matrix',
  icon: <FontAwesomeIcon icon={['fas', 'merge']} />,
  performDryRun,
  inputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'observationTable',
      name: 'Observations table',
      columns: [],
    },
    {
      type: TpETLObjectTypes.TABLE,
      id: 'metadataTable',
      name: 'Metadata table',
      columns: [],
    },
  ],
  outputs: [
    {
      id: 'result',
      name: 'Result matrix',
      type: TpETLObjectTypes.MATRIX,
      description: 'Matrix consisting of observation metadata, feature observations and feature metadata',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  params: [
    {
      id: 'linkColumn',
      name: 'Link column',
      description: 'Column containing feature identifiers used to link the metadata to the main table.',
      paramType: createConfigParamTypeExistingColumn('metadataTable'),
    },
    {
      id: 'observationMetadataColumns',
      name: 'Observation metadata',
      description:
        'Columns from the observation table representing metadata. All columns not representing features or metadata will be dropped.',
      paramType: createConfigParamTypeExistingColumnList('observationTable'),
    },
  ],
};
